/**
 * Sunday evening, in her voice: the week reduced to a verdict, a chart, a
 * superlative, whatever was earned, and a sign-off.
 *
 * Staged, not boxed. This used to be eight small cards in the middle of the
 * screen with „Toliau" underneath, which is the shape of a settings wizard —
 * and a weekly report nobody looks forward to is a weekly report nobody opens.
 * It now runs on the same stage as the first meeting (`stage/Stage.tsx`): full
 * bleed, one figure per screen, swiped through, with her room lit differently
 * on every beat.
 *
 * Slides that have nothing to say still drop out of the sequence entirely
 * rather than rendering an apology. A week with no target has no best-and-worst
 * day to name, and a page that exists only to say so is worse than one fewer
 * page.
 */

import {
  addDays,
  deviceTimeZone,
  localDateString,
  pluralLt,
  startOfWeek,
  trophyByKey,
} from '@gramas/shared'
import { cn, useBackLayer } from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import type { CSSProperties, ReactNode } from 'react'
import { useState } from 'react'
import { db } from '../db/dexie.js'
import { bodyMeasurementsBetween, goalForDate, listAwards } from '../db/repo.js'
import { type CachedLine, personalFor } from '../glitime/lines.js'
import { currentProfile } from '../screens/useProfile.js'
import { Aside, Beat, Figure, Hero, Kicker, Line, Mark, Rise } from '../stage/parts.js'
import { Stage } from '../stage/Stage.js'
import { buildWrapped, type WrappedWeek } from './compute.js'
import {
  awardsTitle,
  DAYS_BOTH,
  DAYS_NO_TARGET,
  DAYS_ONE,
  LEARNED_HEADING,
  NO_AWARDS,
  NO_AWARDS_TITLE,
  NO_TOP_FOOD,
  NO_TOP_FOOD_TITLE,
  PROTEIN_NO_TARGET,
  PROTEIN_TITLE,
  PROTEIN_TITLE_CLEAN,
  proteinAside,
  streakAside,
  totalLine,
  VERDICT_LABEL,
} from './copy.js'
import { useWrappedNote } from './note.js'
import { closeWrapped, useWrappedState } from './store.js'

/** The report for one Monday-anchored week, or undefined while it loads. */
export function useWrappedWeek(weekStart: string | null): WrappedWeek | undefined {
  return useLiveQuery(async () => {
    if (weekStart == null) return undefined
    const weekEnd = addDays(weekStart, 6)
    const [entries, workouts, weights, goal, awards] = await Promise.all([
      db.logEntries.where('logDate').between(weekStart, weekEnd, true, true).toArray(),
      db.workouts.where('logDate').between(weekStart, weekEnd, true, true).toArray(),
      bodyMeasurementsBetween(weekStart, weekEnd),
      goalForDate(weekEnd),
      listAwards(),
    ])
    return buildWrapped({
      weekStart,
      weekEnd,
      entries,
      kcalTarget: goal.kcalTarget,
      proteinTargetG: goal.proteinG,
      weights,
      workouts,
      awards,
      userId: currentProfile()?.id ?? 'anon',
    })
  }, [weekStart])
}

/** The Monday-anchored week the current moment falls in. */
export function currentWeekStart(): string {
  return startOfWeek(localDateString(new Date(), deviceTimeZone()))
}

/**
 * The overlay, wherever it was opened from. Mounted once in the shell so a
 * push deep-link, the Sunday card on Today and the archive list in Tendencijos
 * all reach the same thing.
 */
export function WrappedHost() {
  const weekStart = useWrappedState((state) => state.weekStart)
  const week = useWrappedWeek(weekStart)
  // Prefetched while the first slides play, so by the commentary chapter the
  // model's sentence is usually already here — and cached per week, so the
  // archive rereads cost nothing.
  const note = useWrappedNote(week)
  if (weekStart == null || week == null) return null
  return <Wrapped week={week} note={note} onClose={closeWrapped} />
}

const WEEKDAY_SHORT = ['Sk', 'Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št'] as const

/** "Pn, 08-21" — enough to place a day without spelling out a whole date. */
function dayLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const weekday = WEEKDAY_SHORT[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? ''
  return `${weekday}, ${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** Just the weekday, for a chart axis where the dates are implied. */
function weekdayOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return WEEKDAY_SHORT[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? ''
}

const signed = (value: number): string => (value > 0 ? `+${value}` : `${value}`)

export interface WrappedProps {
  week: WrappedWeek
  /**
   * The model's sentence or two about this specific week, when one arrived —
   * rendered under the scripted commentary, never instead of it. Null costs
   * the slide nothing; the layout has no gap where it would have been.
   */
  note?: string | null
  onClose: () => void
}

type Chapter =
  | 'verdict'
  | 'days'
  | 'food'
  | 'protein'
  | 'streak'
  | 'commentary'
  | 'learned'
  | 'awards'
  | 'signoff'

/** The light in the room, per beat. The verdict slide picks its own below. */
const HUE: Record<Chapter, string> = {
  verdict: 'rgba(60, 45, 150, 0.30)',
  days: 'rgba(30, 70, 130, 0.30)',
  food: 'rgba(105, 30, 95, 0.28)',
  protein: 'rgba(20, 85, 105, 0.30)',
  streak: 'rgba(65, 40, 140, 0.28)',
  commentary: 'rgba(45, 30, 110, 0.34)',
  // Same room as commentary — this is her, still talking, one slide later.
  learned: 'rgba(45, 30, 110, 0.34)',
  awards: 'rgba(115, 85, 20, 0.26)',
  signoff: 'rgba(25, 20, 60, 0.34)',
}

/** Surface key the nightly generator writes this chapter's lines under. */
const LEARNED_SURFACE = 'wrapped_learned'
/** The nightly batch can land a day or two late; the window forgives that. */
const LEARNED_GRACE_DAYS = 2

/**
 * This week's `wrapped_learned` lines, newest first. A line generated before
 * the week started or long after it ended belongs to some other Sunday, so
 * the window is the week itself plus a short grace past `weekEnd` for a
 * nightly batch that ran late rather than the calendar being wrong.
 */
export function learnedLinesFor(week: WrappedWeek, candidates: CachedLine[]): CachedLine[] {
  const start = Date.parse(`${week.weekStart}T00:00:00.000Z`)
  const end = Date.parse(`${addDays(week.weekEnd, LEARNED_GRACE_DAYS)}T23:59:59.999Z`)
  return candidates
    .filter((line) => {
      const at = Date.parse(line.createdAt)
      return at >= start && at <= end
    })
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
}

/**
 * The chapter sequence for one week's report. A slide with nothing to say
 * drops out entirely rather than rendering empty — `days`/`protein`/`streak`
 * already do this for a week with no target, and `learned` follows the same
 * rule for a week the generator had nothing personal to say about.
 */
export function chaptersFor(week: WrappedWeek, learnedCount: number): Chapter[] {
  const chapters: Chapter[] = ['verdict']
  if (week.daysLogged > 0) chapters.push('days')
  chapters.push('food')
  if (week.daysLogged > 0) chapters.push('protein', 'streak')
  chapters.push('commentary')
  if (learnedCount > 0) chapters.push('learned')
  chapters.push('awards', 'signoff')
  return chapters
}

const VERDICT_HUE: Record<WrappedWeek['verdict'], string> = {
  on: 'rgba(55, 110, 40, 0.28)',
  over: 'rgba(120, 45, 60, 0.30)',
  under: 'rgba(30, 65, 130, 0.30)',
  plain: 'rgba(60, 45, 150, 0.30)',
}

export function Wrapped({ week, note = null, onClose }: WrappedProps) {
  // Offline-safe by construction: `personalFor` only ever reads the
  // already-hydrated client cache, never the network.
  const learned = learnedLinesFor(week, personalFor(LEARNED_SURFACE))
  const chapters = chaptersFor(week, learned.length)

  const [index, setIndex] = useState(0)
  const chapter = chapters[Math.min(index, chapters.length - 1)]!

  useBackLayer(true, onClose)

  return (
    <Stage
      label="Savaitės Wrapped"
      count={chapters.length}
      index={index}
      onIndex={setIndex}
      onFinish={onClose}
      finishLabel="Uždaryti"
      onClose={onClose}
      crest="Glitimė"
      // The week the report is about, in the top band rather than as a kicker
      // on the first slide only: eight slides all belong to the same seven
      // days, and the one that says so should not be the one you swiped past.
      note={`${week.weekStart.slice(5)} – ${week.weekEnd.slice(5)}`}
      hue={chapter === 'verdict' ? VERDICT_HUE[week.verdict] : HUE[chapter]}
    >
      {chapter === 'verdict' && <VerdictBeat week={week} />}
      {chapter === 'days' && <DaysBeat week={week} />}
      {chapter === 'food' && <FoodBeat week={week} />}
      {chapter === 'protein' && <ProteinBeat week={week} />}
      {chapter === 'streak' && <StreakBeat week={week} />}
      {chapter === 'commentary' && <CommentaryBeat week={week} note={note} />}
      {chapter === 'learned' && <LearnedBeat lines={learned.slice(0, 2)} />}
      {chapter === 'awards' && <AwardsBeat week={week} />}
      {chapter === 'signoff' && <SignOffBeat week={week} />}
    </Stage>
  )
}

function VerdictBeat({ week }: { week: WrappedWeek }) {
  return (
    <Beat top={<Figure value={week.avgKcal} label="vidurkis" unit="kcal per dieną" />}>
      <Hero delayMs={620}>{VERDICT_LABEL[week.verdict]}</Hero>
      <Rise step={0} delayMs={900} className="flex flex-wrap gap-2">
        <Stat>{week.daysLogged} d. iš 7</Stat>
        {week.gapKcal != null && (
          <Stat tone={week.gapKcal > 0 ? 'warn' : 'good'}>
            {week.gapKcal > 0
              ? `${week.gapKcal} kcal virš tikslo`
              : `${Math.abs(week.gapKcal)} kcal iki tikslo`}
          </Stat>
        )}
        {week.weightChangeKg != null && (
          <Stat>
            Svoris {week.weightChangeKg > 0 ? '+' : '−'}
            {Math.abs(week.weightChangeKg)} kg
          </Stat>
        )}
        {week.workoutCount > 0 && <Stat>Treniruotės ×{week.workoutCount}</Stat>}
      </Rise>
    </Beat>
  )
}

/** A figure small enough to sit beside three others. */
function Stat({ children, tone }: { children: ReactNode; tone?: 'good' | 'warn' }) {
  return (
    <span
      className={cn(
        'tnum rounded-full border px-3 py-1 text-xs',
        tone === 'warn' && 'border-[#ffb020]/35 bg-[#ffb020]/10 text-[#ffcf7a]',
        tone === 'good' &&
          'border-[var(--stage-figure)]/35 bg-[var(--stage-figure)]/10 text-[var(--stage-figure)]',
        tone == null && 'border-[var(--stage-line)] bg-white/[0.04] text-[var(--stage-dim)]',
      )}
    >
      {children}
    </span>
  )
}

/**
 * The week as seven columns.
 *
 * The single biggest thing the old report was missing: it named a best and a
 * worst day in words and never showed the shape they came from. Empty days are
 * drawn as stubs rather than omitted — the gaps are half the story — and the
 * target, when there is one, is a line the bars are read against instead of a
 * number in a sentence.
 */
function DaysBeat({ week }: { week: WrappedWeek }) {
  return (
    <Beat top={<DaysChart week={week} />}>
      <Kicker>Savaitė</Kicker>
      <Hero delayMs={80}>Dienos su vardais</Hero>

      {week.bestDay != null ? (
        <>
          <Rise step={0} delayMs={900} className="flex flex-wrap gap-2">
            <Stat tone="good">
              Tiksliausia: {dayLabel(week.bestDay.date)}
              {week.bestDay.gapKcal != null && ` (${signed(week.bestDay.gapKcal)})`}
            </Stat>
            {week.worstDay != null && (
              <Stat tone="warn">
                Garsiausia: {dayLabel(week.worstDay.date)}
                {week.worstDay.gapKcal != null && ` (${signed(week.worstDay.gapKcal)})`}
              </Stat>
            )}
          </Rise>
          <Aside delayMs={1050}>{week.worstDay != null ? DAYS_BOTH : DAYS_ONE}</Aside>
        </>
      ) : (
        <Line delayMs={900}>{DAYS_NO_TARGET}</Line>
      )}
    </Beat>
  )
}

function DaysChart({ week }: { week: WrappedWeek }) {
  const target = week.kcalTarget
  const peak = Math.max(...week.days.map((d) => d.kcal), target ?? 0, 1)
  // Headroom above the tallest bar, so a record day is not flush with the top.
  const ceiling = peak * 1.12

  return (
    <div className="relative h-52">
      <div className="flex h-full items-end gap-1.5">
        {week.days.map((day, i) => {
          const over = target != null && day.kcal > target
          const best = day.date === week.bestDay?.date
          const worst = day.date === week.worstDay?.date
          return (
            <div key={day.date} className="flex h-full flex-1 flex-col items-center justify-end">
              <span
                className={cn(
                  'tnum mb-1 text-[10px]',
                  day.logged ? 'text-[var(--stage-dim)]' : 'text-transparent',
                )}
              >
                {day.logged ? day.kcal : 0}
              </span>
              <div
                className={cn(
                  'stage-bar w-full rounded-md',
                  day.logged
                    ? over
                      ? 'bg-[var(--stage-glow)] shadow-[0_0_18px_-4px_var(--stage-glow)]'
                      : 'bg-[var(--stage-figure)] shadow-[0_0_18px_-4px_var(--stage-figure)]'
                    : 'bg-[var(--stage-line)]',
                )}
                style={
                  {
                    height: day.logged ? `${Math.max(4, (day.kcal / ceiling) * 100)}%` : '4px',
                    '--i': i,
                  } as CSSProperties
                }
              />
              {/* The two days she names below are lit here too, so the
                    superlatives point at something rather than being read as
                    a separate list of dates. */}
              <span
                className={cn(
                  'mt-1.5 text-[10px]',
                  best && 'font-semibold text-[var(--stage-figure)]',
                  worst && 'font-semibold text-[#ffcf7a]',
                  !best && !worst && 'text-[var(--stage-mute)]',
                )}
              >
                {weekdayOf(day.date)}
              </span>
            </div>
          )
        })}
      </div>
      {/* Drawn over the bars, not under them: a target line that disappears
            behind the days it is measuring is decoration. */}
      {target != null && (
        <div
          aria-hidden="true"
          className="stage-rise pointer-events-none absolute inset-x-0 border-[var(--stage-ink)]/45 border-t border-dashed"
          style={{ bottom: `${(target / ceiling) * 100}%`, '--d': '760ms' } as CSSProperties}
        >
          <span className="-top-2 tnum absolute left-0 rounded bg-[var(--stage-bg)]/75 px-1 text-[10px] text-[var(--stage-ink)]/70">
            tikslas {target}
          </span>
        </div>
      )}
    </div>
  )
}

function FoodBeat({ week }: { week: WrappedWeek }) {
  if (week.topFood == null) {
    return (
      // Nothing came back twice, so the picture is five things and no two of
      // them the same — the shape of the week she is about to comment on.
      <Beat
        top={
          <div aria-hidden="true" className="flex items-end gap-2.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Rise key={i} step={i}>
                <span className="block size-9 rounded-lg border border-[var(--stage-line)] bg-white/[0.04]" />
              </Rise>
            ))}
          </div>
        }
      >
        <Kicker>Savaitės pasirinkimas</Kicker>
        <Hero delayMs={80}>{NO_TOP_FOOD_TITLE}</Hero>
        <Line delayMs={480}>{NO_TOP_FOOD}</Line>
      </Beat>
    )
  }
  return (
    <Beat
      top={
        <Figure
          value={week.topFood.count}
          prefix="×"
          unit={`${pluralLt(week.topFood.count, 'kartą', 'kartus', 'kartų')} per savaitę`}
          tone="glow"
        />
      }
    >
      <Kicker>Savaitės pasirinkimas</Kicker>
      <Hero delayMs={620}>{week.topFood.name}</Hero>
      <Aside delayMs={950}>{week.topFood.aside}</Aside>
    </Beat>
  )
}

function ProteinBeat({ week }: { week: WrappedWeek }) {
  const { avgG, targetG, daysMet } = week.protein
  const progress = targetG == null || week.daysLogged === 0 ? 0 : daysMet / week.daysLogged
  return (
    <Beat
      top={
        <div className="stage-halo flex items-center gap-5">
          <Arc
            progress={progress}
            label={targetG == null ? '—' : `${daysMet}/${week.daysLogged}`}
          />
          <div>
            <p className="tnum font-display font-semibold text-[clamp(2.4rem,12vw,3.4rem)] text-[var(--stage-ink)] leading-none tracking-[-0.03em]">
              {avgG}
              <span className="ml-1 font-normal text-[var(--stage-mute)] text-lg">g</span>
            </p>
            <p className="mt-1 text-[var(--stage-dim)] text-sm">vidurkis per dieną</p>
            {targetG != null && (
              <p className="tnum mt-0.5 text-[var(--stage-mute)] text-xs">tikslas {targetG} g</p>
            )}
          </div>
        </div>
      }
    >
      <Kicker>Baltymai</Kicker>
      {targetG != null ? (
        <>
          <Hero delayMs={520}>
            {daysMet >= week.daysLogged ? PROTEIN_TITLE_CLEAN : PROTEIN_TITLE}
          </Hero>
          <Aside delayMs={820}>{proteinAside(week)}</Aside>
        </>
      ) : (
        <Line delayMs={520}>{PROTEIN_NO_TARGET}</Line>
      )}
    </Beat>
  )
}

/** Days that reached the protein target, drawn as an arc rather than a ratio. */
function Arc({ progress, label }: { progress: number; label: string }) {
  const size = 108
  const stroke = 8
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - Math.min(1, Math.max(0, progress)))
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        className="-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--stage-line)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--stage-cold)"
          strokeWidth={stroke}
          strokeLinecap="round"
          className="stage-arc"
          strokeDasharray={circumference}
          style={{ '--c': circumference, '--o': offset, strokeDashoffset: offset } as CSSProperties}
        />
      </svg>
      <span className="tnum absolute inset-0 grid place-items-center font-display font-semibold text-[var(--stage-ink)] text-lg">
        {label}
      </span>
    </div>
  )
}

function StreakBeat({ week }: { week: WrappedWeek }) {
  return (
    <Beat
      top={
        <div className="flex flex-col gap-5">
          <Figure
            value={week.longestRun}
            unit={`${pluralLt(week.longestRun, 'diena', 'dienos', 'dienų')} iš eilės`}
            label="ilgiausia serija"
            tone="ink"
          />
          <div className="flex gap-2.5" aria-hidden="true">
            {week.days.map((day, i) => (
              <Rise key={day.date} step={i} delayMs={620}>
                <span
                  className={cn(
                    'block size-4 rounded-full',
                    day.logged
                      ? 'bg-[var(--stage-figure)] shadow-[0_0_14px_-1px_var(--stage-figure)]'
                      : 'border border-[var(--stage-line)] bg-transparent',
                  )}
                />
              </Rise>
            ))}
          </div>
        </div>
      }
    >
      <Kicker>Nuoseklumas</Kicker>
      <Aside delayMs={900}>{streakAside(week)}</Aside>
    </Beat>
  )
}

function CommentaryBeat({ week, note }: { week: WrappedWeek; note: string | null }) {
  return (
    // The one slide that is only her talking, so what stands above it is a
    // quotation mark and nothing else.
    <Beat
      top={
        <Rise>
          <span
            aria-hidden="true"
            className="stage-breathe block font-display text-[7rem] text-[var(--stage-glow)]/45 leading-[0.6]"
          >
            „
          </span>
        </Rise>
      }
    >
      <Kicker>Mano nuomonė</Kicker>
      <GhostQuote>{week.commentary}</GhostQuote>
      {/* Her extra sentence about this specific week, when the line to the
          beyond was up on Sunday. Arrives late and lower, like an afterthought
          she decided to say out loud after all. */}
      {note != null && <Aside delayMs={1400}>{note}</Aside>}
    </Beat>
  )
}

/** Her paragraph, set as the slide rather than under a heading on it. */
function GhostQuote({ children }: { children: string }) {
  return (
    <Hero delayMs={80} className="text-[clamp(1.4rem,6.4vw,2rem)] leading-[1.28]">
      {children}
    </Hero>
  )
}

/**
 * What she noticed about this specific account this week — one or two lines
 * out of the nightly personal batch, not the shared catalogue. Same room as
 * `CommentaryBeat`, same quote mark, because this is still her talking; the
 * only difference is the heading names the chapter instead of a category.
 */
function LearnedBeat({ lines }: { lines: CachedLine[] }) {
  const [first, second] = lines
  if (first == null) return null
  return (
    <Beat
      top={
        <Rise>
          <span
            aria-hidden="true"
            className="stage-breathe block font-display text-[7rem] text-[var(--stage-glow)]/45 leading-[0.6]"
          >
            „
          </span>
        </Rise>
      }
    >
      <Kicker>{LEARNED_HEADING}</Kicker>
      <GhostQuote>{first.text}</GhostQuote>
      {second != null && <Aside delayMs={1400}>{second.text}</Aside>}
    </Beat>
  )
}

function AwardsBeat({ week }: { week: WrappedWeek }) {
  return (
    <Beat top={<AwardDiscs count={week.awards.length} />}>
      <Kicker>Šios savaitės laimikis</Kicker>
      {week.awards.length > 0 ? (
        <>
          <Hero delayMs={80}>{awardsTitle(week.awards.length)}</Hero>
          <div className="mt-1 flex flex-col gap-2">
            {week.awards.map((award, i) => {
              const def = trophyByKey(award.key)
              return (
                <Rise key={award.key} step={i} delayMs={460}>
                  <div className="rounded-2xl border border-[#ffd166]/30 bg-[#ffd166]/[0.07] px-4 py-3">
                    <p className="font-display font-semibold text-[#ffd98a] text-[15px]">
                      {def?.title ?? award.key}
                    </p>
                    {def != null && (
                      <p className="mt-0.5 text-[var(--stage-dim)] text-sm leading-[1.45]">
                        {def.description}
                      </p>
                    )}
                  </div>
                </Rise>
              )
            })}
          </div>
        </>
      ) : (
        <>
          <Hero delayMs={80}>{NO_AWARDS_TITLE}</Hero>
          <Line delayMs={480}>{NO_AWARDS}</Line>
        </>
      )}
    </Beat>
  )
}

/**
 * One lit disc per trophy, or a single empty ring for a week that earned
 * none — the same silhouette the cabinet in Tendencijos uses for a trophy
 * nobody has found yet.
 */
function AwardDiscs({ count }: { count: number }) {
  if (count === 0) {
    return (
      <Rise>
        <span
          aria-hidden="true"
          className="block size-16 rounded-full border border-[var(--stage-line)] border-dashed"
        />
      </Rise>
    )
  }
  return (
    <div aria-hidden="true" className="flex flex-wrap items-center gap-3.5">
      {Array.from({ length: count }, (_, i) => i).map((i) => (
        <Rise key={i} step={i}>
          <span className="grid size-16 place-items-center rounded-full border border-[#ffd166]/45 bg-[#ffd166]/10 shadow-[0_0_34px_-8px_#ffd166]">
            {/* A ring inside a ring, not a dot inside a ring: two lit discs
                side by side read as a pair of eyes, and this room already has
                a pair of those. */}
            <span className="stage-breathe block size-7 rounded-full border border-[#ffd98a]/70" />
          </span>
        </Rise>
      ))}
    </div>
  )
}

function SignOffBeat({ week }: { week: WrappedWeek }) {
  return (
    // Closing on the same mark the first meeting opened and closed on: the
    // report is over, and what is left in the room is her.
    <Beat top={<Mark />}>
      <Kicker>Tiek žinių</Kicker>
      <Hero delayMs={80} className="text-[clamp(1.5rem,7vw,2.2rem)] leading-[1.25]">
        {week.signOff}
      </Hero>
      <Line delayMs={620}>{totalLine(week.totalKcal)}</Line>
    </Beat>
  )
}
